<template>
  <stop-display
    :location-name="stopLocationName"
    :is-location-undecided="stopLocationIsUndecided"
    :inventory="!canCreateStops ? displayInventory : []"
  >
    <template #top-right>
      <vs-button
        v-if="showEditButton"
        icon="mdi-pencil"
        small
        type="secondary"
        class="mr-2"
        @click="onClickEdit"
      />
      <v-tooltip :open-delay="250" bottom :disabled="canConfirmStop">
        <template #activator="{ on }">
          <div v-on="on">
            <vs-button
              v-if="canCreateStops"
              icon="mdi-skip-next"
              small
              type="secondary"
              label="Skip"
              data-test="skip-button"
              :disabled="!canConfirmStop"
              @click="onClickConfirmStop(true)"
            />
          </div>
        </template>
        <span>{{ tooltipText }}</span>
      </v-tooltip>
    </template>
    <template #cards>
      <v-tooltip :open-delay="250" bottom :disabled="canConfirmStop">
        <template #activator="{ on }">
          <div v-on="on">
            <stop-card
              v-if="canCreateStops"
              :icon="cardIcon"
              :title="cardText"
              :subtitles="displayInventory"
              data-test="confirm-stop-card"
              :disabled="!canConfirmStop"
              :state="!canConfirmStop ? 'disabled' : 'incomplete'"
              @click="onClickConfirmStop"
            />
          </div>
        </template>
        <span>{{ tooltipText }}</span>
      </v-tooltip>
      <stop-card
        v-for="form in getRequiredFormsForStopType(stop.stopType)"
        :key="form.formUuid"
        icon="mdi-clipboard-text-outline"
        state="disabled"
        :title="form.name"
        disabled
        data-test="stop-form-card"
      />
    </template>
  </stop-display>
</template>

<script setup lang="ts">
import { InventoryReceiptType, RequestedStop } from '@ironsight/types'
import { computed, toRef } from 'vue'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { useJobStopsAndReceiptsPermissions } from './useJobStopsAndReceiptsPermissions'
import { buildDisplayInventory, buildInventoryDisplayString } from '@/utils/inventories'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'
import StopCard from '@/components/tickets/StopsAndInventories/StopCard.vue'
import { useJobStopForms } from '@/components/tickets/StopsAndInventories/useJobStopForms'
import StopDisplay from '@/components/tickets/StopsAndInventories/StopDisplay.vue'
import VsButton from '@/components/vision/VsButton.vue'

const props = defineProps<{
  stop: RequestedStop
  jobNumber: number
  isPickup: boolean
  showEditButton: boolean
}>()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()

const stopRef = computed(() => props.stop)
const jobNumberRef = toRef(props, 'jobNumber')
const locationOwnerIdRef = computed(() => props.stop.locationOwnerId)

const { getRequiredFormsForStopType, isAnyStopInProgress } = useJobStopForms(jobNumberRef)

const { canCreateStops } = useJobStopsAndReceiptsPermissions(jobNumberRef, locationOwnerIdRef)

const { allInventoryTypes } = useInventoryTypes()

const cardIcon = computed(() => (props.isPickup ? 'mdi-upload' : 'mdi-download'))
const cardText = computed(() => (props.isPickup ? 'Confirm Pickup' : 'Confirm DropOff'))
const canConfirmStop = computed(() => !isAnyStopInProgress.value && !stopLocationIsUndecided.value)
const tooltipText = computed(() =>
  stopLocationIsUndecided.value
    ? 'The requested location must be set before this stop can be actioned.'
    : 'All stop forms must be filled out before another stop can be confirmed.',
)

const displayInventory = computed(() => {
  return buildDisplayInventory(props.stop.inventory, allInventoryTypes.value ?? []).map(
    buildInventoryDisplayString,
  )
})

const stopLocationIsUndecided = computed(() => props.stop.locationId == null)
const stopLocationName = computed(() => {
  if (stopLocationIsUndecided.value) {
    return 'Undecided'
  } else {
    return props.stop.locationName ?? 'Unknown'
  }
})

const onClickConfirmStop = (isSkipping = false) => {
  if (!canCreateStops.value) return

  jobBottomSheetStatesStore.setJobBottomSheetStates({
    stopConfirmationBottomSheetStates: {
      isVisible: true,
      requestedStop: stopRef.value,
      actualStop: null,
      isSkipping,
      receiptType: props.isPickup ? InventoryReceiptType.Receiver : InventoryReceiptType.Originator,
    },
  })
}

const onClickEdit = () => {
  jobBottomSheetStatesStore.setJobBottomSheetStates({
    requestedStopFormBottomSheetVisible: true,
  })
}
</script>
